import { CheckCircle, Info, Warning, XCircle } from '@phosphor-icons/react';
import { useEffect, useId, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { formatNumber, fullPrecision } from './format';
import k from './kit.module.css';
import { fromSi, parseQuantity, UNITS, type Quantity } from './units';
import type { ZoneNumber } from '../model/types';

const cx = (...c: Array<string | false | undefined>) => c.filter(Boolean).join(' ');

export function Button({ variant = 'secondary', icon, pressed, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost'; icon?: ReactNode; pressed?: boolean }) {
  return (
    <button type="button" {...rest} aria-pressed={pressed} className={cx(k.btn, variant === 'primary' && k.primary, variant === 'ghost' && k.ghost, !children && k.icon, pressed && k.pressed, className)}>
      {icon}
      {children}
    </button>
  );
}

export function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className={k.section}>
      <div className={k.sectionHead}>
        <span>{title}</span>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Field({ label, symbol, note, error, children, htmlFor, wide }: { label: string; symbol?: string; note?: ReactNode; error?: string | undefined; children: ReactNode; htmlFor?: string; wide?: boolean }) {
  return (
    <div className={cx(k.field, wide && k.wide)}>
      <label className={k.label} htmlFor={htmlFor}>
        {label}
        {symbol && <abbr>{symbol}</abbr>}
      </label>
      <div className={k.control}>{children}</div>
      {error ? <div className={k.error} role="alert">{error}</div> : note ? <div className={k.note}>{note}</div> : null}
    </div>
  );
}

/** Number input in engineers' units. Commits SI on Enter or blur; Escape reverts. Never clamps. */
export function NumberField({
  label, symbol, valueSi, quantity, unit, onUnitChange, onCommit, note, error, disabled, min, allowEmpty,
}: {
  label: string; symbol?: string; valueSi: number | undefined; quantity: Quantity; unit?: string;
  onUnitChange?: (u: string) => void; onCommit: (si: number | undefined) => void; note?: ReactNode; error?: string | undefined;
  disabled?: boolean; min?: number; allowEmpty?: boolean;
}) {
  const id = useId();
  const units = UNITS[quantity];
  const shownUnit = unit ?? units[0]!.label;
  const display = valueSi === undefined ? '' : String(Number(fromSi(valueSi, quantity, shownUnit).toPrecision(6)));
  const [draft, setDraft] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | undefined>();
  useEffect(() => { setDraft(null); setProblem(undefined); }, [valueSi, shownUnit]);

  const commit = () => {
    if (draft === null) return;
    if (!draft.trim() && allowEmpty) { setDraft(null); setProblem(undefined); onCommit(undefined); return; }
    const r = parseQuantity(draft, quantity, shownUnit);
    if ('error' in r) { setProblem(r.error); return; }
    if (min !== undefined && r.si < min) { setProblem(`Enter a value of at least ${formatNumber(fromSi(min, quantity, shownUnit))} ${shownUnit}.`); return; }
    setProblem(undefined);
    setDraft(null);
    if (r.unitLabel !== shownUnit && onUnitChange) onUnitChange(r.unitLabel);
    onCommit(r.si);
  };

  return (
    <Field label={label} {...(symbol ? { symbol } : {})} htmlFor={id} note={note} error={problem ?? error}>
      <input
        id={id} className={cx(k.input, k.num, !shownUnit && k.noUnit)} inputMode="decimal" disabled={disabled} aria-invalid={!!(problem ?? error)}
        value={draft ?? display} title={valueSi !== undefined ? `${fullPrecision(fromSi(valueSi, quantity, shownUnit))} ${shownUnit}` : undefined}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { commit(); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { setDraft(null); setProblem(undefined); (e.target as HTMLInputElement).blur(); }
        }}
      />
      {onUnitChange && units.length > 1 ? (
        <select className={k.suffix} aria-label={`${label} unit`} value={shownUnit} onChange={(e) => onUnitChange(e.target.value)}>
          {units.map((u) => <option key={u.label} value={u.label}>{u.label}</option>)}
        </select>
      ) : shownUnit ? (
        <span className={k.suffix} aria-hidden="true">{shownUnit}</span>
      ) : null}
    </Field>
  );
}

export function TextField({ label, value, onCommit, placeholder, multiline, note }: { label: string; value: string; onCommit: (v: string) => void; placeholder?: string; multiline?: boolean; note?: ReactNode }) {
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => setDraft(null), [value]);
  const commit = () => { if (draft !== null && draft !== value) onCommit(draft); setDraft(null); };
  const common = { id, value: draft ?? value, placeholder, onChange: (e: { target: { value: string } }) => setDraft(e.target.value), onBlur: commit };
  return (
    <Field label={label} htmlFor={id} note={note}>
      {multiline ? <textarea {...common} className={k.textarea} rows={2} /> : <input {...common} className={k.input} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setDraft(null); (e.target as HTMLInputElement).blur(); } }} />}
    </Field>
  );
}

export function SelectField<T extends string>({ label, value, options, onChange, note, symbol }: { label: string; value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void; note?: ReactNode; symbol?: string }) {
  const id = useId();
  return (
    <Field label={label} htmlFor={id} note={note} {...(symbol ? { symbol } : {})}>
      <select id={id} className={k.select} value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T | undefined; options: Array<{ value: T; label: string }>; onChange: (v: T) => void; label: string }) {
  return (
    <div className={k.seg} role="group" aria-label={label}>
      {options.map((o) => <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );
}

export function ZoneSwatch({ zone }: { zone: ZoneNumber | 'ne' }) {
  return <span className={cx(k.swatch, zone === 0 ? k.z0 : zone === 1 ? k.z1 : zone === 2 ? k.z2 : k.zne)} aria-hidden="true" />;
}

export function ZoneChip({ zone, label }: { zone: ZoneNumber | 'ne' | null; label: string }) {
  return (
    <span className={k.chip}>
      {zone !== null && <ZoneSwatch zone={zone} />}
      {label}
    </span>
  );
}

const STATUS_ICON = { fail: XCircle, error: XCircle, warning: Warning, pass: CheckCircle, ok: CheckCircle, outside: Info, info: Info } as const;

export function StatusChip({ status, label }: { status: keyof typeof STATUS_ICON; label?: string }) {
  const Icon = STATUS_ICON[status];
  return (
    <span className={cx(k.chip, k.status)} data-s={status}>
      <Icon size={14} weight={status === 'outside' || status === 'info' ? 'regular' : 'fill'} aria-hidden="true" />
      {label ?? status}
    </span>
  );
}

export function Ref({ children }: { children: ReactNode }) {
  return <span className={k.ref}>{children}</span>;
}

export { k as kitStyles, cx };
